import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { MyConfigService } from '../../../../services/my-config.service';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { GateMode, OrganizationGatesVM } from '../../../../models/orderModel';
import Hls from 'hls.js';
import { OpenGateFormComponent } from './open-gate-form/open-gate-form.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../../environments/environment';
import { DisplayTestsComponent } from '../parking-display/display-tests/display-tests.component';

@Component({
  selector: 'app-back-office24-gates',
  templateUrl: './back-office24-gates.component.html',
  styleUrl: './back-office24-gates.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BackOffice24GatesComponent implements OnInit, OnDestroy {
  componentDestroyed$: Subject<boolean> = new Subject();
  loading = false;
  activeRequests = 0;
  @Input() organization: MyConfigOrganization
  private orgSubscription: Subscription = new Subscription();
  cameras: any[];
  gates: any[] = [];
  parkings: any[] = [];
  selectedParkings: string[] = [];
  filteredCameras: any[] = [];
  parkingDisplay: string = "https://parking-display-test.mangocliff-b7a35cda.swedencentral.azurecontainerapps.io/";
  private hlsInstances: { [key: string]: Hls } = {};
  private cameraStates: { [key: string]: boolean } = {};
  gateModes: any[] = [
    { value: "AUTO", name: "Auto" },
    { value: "FREE", name: "Free Entrance" },
    { value: "BLQP", name: "Block Open" },
    { value: "BLCL", name: "Block Closed" }
  ];

  constructor(
    private myConfigService: MyConfigService,
    private BackOfficeService: BackOfficeService,
    private route: ActivatedRoute,
    public backOffice24StatesService: BackOffice24StatesService,
    public dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => {
        this.organization = org
        org.config.parkings.forEach((element: any) => {
          this.parkings.push({
            value: element.parkingId,
            name: element.parkingName
          });
        });
      }
    );
    this.getOrganizationGates();
  }

  setLoadingState(state: boolean) {
    this.loading = state;
  }

  incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  decrementRequests() {
    if (this.activeRequests > 0) {
      this.activeRequests--;
    }
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  filterGates() {
    if (this.selectedParkings.length > 0) {
      const selectedParkingSet = new Set(this.selectedParkings);
      this.filteredCameras = this.cameras.filter(cam =>
        cam.parkings?.some(parking => selectedParkingSet.has(parking.id))
      );
    } else {
      this.filteredCameras = this.cameras;
    }
  }

  getOrganizationGates() {
    this.incrementRequests();
    this.BackOfficeService.getOrganizationGates(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(gates => {
        this.gates = gates;

        this.cameras = gates.flatMap((gate: any) =>
          gate.cameras.map((camera: any) => ({
            ...gate,
            camId: camera.sensorId,
            gateType: camera.type,
            // cameras: undefined
          }))
        ).map(gate => {
          const { cameras, ...rest } = gate;
          return rest;
        });

        var newCameras = new Set();
        this.parkings.forEach((parking) => {
          parking.cameras = this.cameras.filter((camera) => camera.parkings?.some(camParking => camParking.id === parking.value));
          parking.cameras.sort((a, b) => {
            if (a.gateType === 'Checkin') return -1;
            if (b.gateType === 'Checkout') return 1;
            return 0;
          });

          parking.cameras.forEach((camera) => {
            newCameras.add(camera);
          });
        })

        this.cameras = [...newCameras];

        const gateModePromises = this.cameras.map(gate => {
          return this.getGateMode(gate).then(mode => {
            gate.mode = mode;
            return gate;
          });
        });

        // Wait for all gate modes to be fetched
        Promise.all(gateModePromises).then(() => {
          console.log('All gate modes loaded:', this.cameras);
          this.filteredCameras = [...this.cameras];
        });

        console.log(this.cameras);

        this.filteredCameras = this.cameras;
        this.decrementRequests();
      });
  }

  getGateMode(gate: any): Promise<string | null> {
    return new Promise((resolve) => {
      this.BackOfficeService.getGateMode(gate.name)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (gateMode) => {
            resolve(gateMode ? gateMode : null); // Default to 'AUTO' if null
          },
          error: (error) => {
            console.error('Error fetching gate mode:', error);
            resolve(null); // Default to 'AUTO' on error
          }
        });
    });
  }

  setGateMode(gate: any, mode: GateMode) {
    this.BackOfficeService.setGateMode(gate.name, mode)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(gateMode => {
        if (gateMode) {
          gate.mode = mode;
        }
      });
  }

  showDriveUpQRCode(cam: any) {
    this.BackOfficeService.showDriveUpQRCode(cam.id)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  openGateForm(cam: any) {
    const dialogRef = this.dialog.open(OpenGateFormComponent, {
      data: {
        camera: cam,
        orgId: this.organization.organizationId
      },
    });
  }

  startStream(camId: string): void {
    const video = document.getElementById(camId) as HTMLVideoElement;
    if (!video) return;

    // Stop existing stream if any basically like toggle
    // if (this.hlsInstances[camId]) {
    //   this.hlsInstances[camId].destroy();
    //   delete this.hlsInstances[camId];
    //   this.cameraStates[camId] = false;
    //   return;
    // }

    const streamUrl = this.BackOfficeService.getCameraStreamUrl(camId);

    if (Hls.isSupported()) {
      const hls = new Hls({
        liveSyncDuration: 2,
        liveMaxLatencyDuration: 5,
        maxBufferLength: 4,
        maxMaxBufferLength: 8,
      });
      this.hlsInstances[camId] = hls;

      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().then(() => {
          this.cameraStates[camId] = true;
        });
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // For Safari
      video.src = streamUrl;
      video.addEventListener('loadedmetadata', () => {
        video.play().then(() => {
          this.cameraStates[camId] = true;
        });
      });
    }
  }

  stopStream(camId: string): void {
    const video = document.getElementById(camId) as HTMLVideoElement;
    if (!video) return;

    const hls = this.hlsInstances[camId];
    if (hls) {
      hls.destroy();
      delete this.hlsInstances[camId];
    }

    video.pause();
    video.src = '';
    this.cameraStates[camId] = false;
  }

  enlargeVideo(videoElement: HTMLVideoElement) {
    if (videoElement.requestFullscreen) {
      videoElement.requestFullscreen();
    } else if ((videoElement as any).webkitRequestFullscreen) {
      (videoElement as any).webkitRequestFullscreen();
    } else if ((videoElement as any).msRequestFullscreen) {
      (videoElement as any).msRequestFullscreen();
    }
  }

  refreshGates() {
    this.getOrganizationGates();
  }

  getGateType(gateType: string) {
    if (gateType === 'Checkin') {
      return 'Infart';
    } else if (gateType === 'Checkout') {
      return 'Utfart';
    }
    return 'Unknown';
  }

  ngOnDestroy(): void {
    Object.values(this.hlsInstances).forEach(hls => {
      if (hls) {
        hls.destroy();
      }
    });

    this.cameras?.forEach(cam => {
      const video = document.getElementById(cam.camId) as HTMLVideoElement;
      if (video) {
        video.pause();
        video.src = '';
        video.load();
      }
    });

    if (this.orgSubscription) {
      this.orgSubscription.unsubscribe();
    }
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  openDisplayTests(gateId: string, display: any) {
    console.log(display);
    const dialogRef = this.dialog.open(DisplayTestsComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      width: 'auto',
      height: 'auto',
      data: {
        gateId: gateId,
        display: display
      },
    });
  }
}
